import { Dispatch } from 'react';
import { RESET_VEHICLE, SET_DATA_MODEL_VALUE } from '../store/ActionKeys';
import { genericRequest, setSelectionValue } from '../store/Actions';
import { updatePackages } from './shared';
import {
    DMR,
    IAction,
    Model,
    Package,
    PackageInfo,
    ProductNames,
    Selection,
    VehicleQualifier
} from '../interfaces/interfaces';
import { IConfigProps } from '../context/ConfigContext';
import { apiGatewayBase, wso2header } from '../constants';
import { IUserContextProps } from '../context/UserContext';

const popularManufacturers: string[] = [
    'Alfa Romeo',
    'Audi',
    'BMW',
    'Chevrolet',
    'Chrysler',
    'Citroën',
    'Dacia',
    'Daewoo',
    'DS',
    'Fiat',
    'Ford',
    'Honda',
    'Hyundai',
    'Jaguar',
    'Jeep',
    'KGM',
    'Kia',
    'Land Rover',
    'Mazda',
    'Mercedes-Benz',
    'Mini',
    'Mitsubishi',
    'Nissan',
    'Opel',
    'Peugeot',
    'Porsche',
    'Renault',
    'Saab',
    'Seat',
    'Skoda',
    'Smart',
    'Subaru',
    'Suzuki',
    'Tesla',
    'Toyota',
    'Volvo',
    'VW',
    'Lexus',
    'Lynk & Co',
    'Cupra',
    'Maxus',
    'MG',
    'Omoda',
    'Polestar',
    'Aiways',
    'NIO',
    'BYD',
    'Xpeng',
    'Hongqi',
    'JAC',
    'Voyah'
];

export const excludedManufacturers: string[] = [
    'aston martin',
    'alpina',
    'bentley',
    'bugatti',
    'cisitalia,',
    'de tomaso',
    'delahaye',
    'delorean',
    'ferrari',
    'lamborghini',
    'maserati',
    'mclaren',
    'rolls-royce'
];

type DropdownOption = { label: string; value: string };

export const getManufactors = async (): Promise<DropdownOption[]> => {
    const data: Array<{ fabrikatNavn: string; topFabrikatId: string }> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/makes?type=BIL`,
        headers: wso2header
    });
    return data
        .filter((x) => popularManufacturers.includes(x.fabrikatNavn))
        .map((x) => ({
            label: x.fabrikatNavn,
            value: String(x.topFabrikatId)
        }));
};

export const getFuels = async (topFabrikatId: string): Promise<DropdownOption[]> => {
    const data: Array<{ drivmiddelTxt: string; drivmiddelId: string }> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/fuels?type=BIL&plateType=P&topFabrikatId=${topFabrikatId}`,
        headers: wso2header
    });
    return data.map((x) => ({
        label: x.drivmiddelTxt,
        value: x.drivmiddelId
    }));
};

export const getModels = async (topFabrikatId: string, fuel: string): Promise<DropdownOption[]> => {
    const data: Array<{ modelNavn: string; topModelId: string }> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/models?plateType=P&topFabrikatId=${topFabrikatId}&fuel=${fuel}`,
        headers: wso2header
    });
    return data.map((x) => ({
        label: x.modelNavn,
        value: String(x.topModelId)
    }));
};

export const getYears = async (topModelId: string, fuel: string): Promise<DropdownOption[]> => {
    const data: Array<{ aargangNysalg: string }> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/years?plateType=P&topModelId=${topModelId}&fuel=${fuel}`,
        headers: wso2header
    });
    return data.map((x) => ({
        label: x.aargangNysalg,
        value: x.aargangNysalg
    }));
};

export const getEngines = async (topModelId: string, fuel: string, year: string): Promise<DropdownOption[]> => {
    const data: Array<{ motorstoerrelse: string }> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/engines?plateType=P&topModelId=${topModelId}&fuel=${fuel}&year=${year}`,
        headers: wso2header
    });
    return data.map((x) => ({
        label: x.motorstoerrelse,
        value: x.motorstoerrelse
    }));
};

export const getPowers = async (
    topModelId: string,
    fuel: string,
    year: string,
    engine: string
): Promise<DropdownOption[]> => {
    const data: Array<{ maxEffHkBetegn: string; maksimalEffektKw: string }> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/powers?plateType=P&topModelId=${topModelId}&fuel=${fuel}&year=${year}&engine=${engine}`,
        headers: wso2header
    });
    return data.map((x) => ({
        label: String(x.maxEffHkBetegn),
        value: String(x.maksimalEffektKw)
    }));
};

export const getVariants = async (
    topModelId: string,
    fuel: string,
    year: string,
    engine: string,
    power: string
): Promise<any> => {
    return await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/variants?rulebasedVariantSelection=true&plateType=P&topModelId=${topModelId}&fuel=${fuel}&year=${year}&engine=${engine}&power=${power}`,
        headers: wso2header
    });
};

/**
 * Verifies car and updates status in store.
 */
export const getCarVerification = async ({
    config,
    dispatch,
    selection,
    user,
    logGetCarVerificationError
}: {
    config: IConfigProps;
    dispatch: Dispatch<IAction>;
    selection: Selection;
    user: IUserContextProps;
    logGetCarVerificationError: (errorMessage: string) => void;
}) => {
    try {
        const cprNo = user.stakeholder?.cprNo ?? selection.cprNo;
        const queryParam = selection.registrationNumber
            ? `?regNr=${selection.registrationNumber}`
            : selection.viNumber
              ? '?regNr=ZNTGDAD' // Dummy call when viNumber is used
              : `?brandId=${selection.manufactor}&modelId=${selection.model}&year=${selection.vintage}`;
        const endpoint = user.loggedIn
            ? {
                  ...config.endpoint.carVerification,
                  url: `${config.endpoint.carVerification.url}${queryParam}`
              }
            : {
                  ...config.endpoint.carVerificationOpenApi,
                  url: `${config.endpoint.carVerificationOpenApi.url}${queryParam}&cprNr=${cprNo}`
              };
        const data = await genericRequest(endpoint);

        dispatch({
            type: SET_DATA_MODEL_VALUE,
            payload: {
                productKey: ProductNames.CAR,
                dataKey: 'status',
                value: data
            }
        });
    } catch (error: any) {
        logGetCarVerificationError(error.message);
    }
};

export const resetCarVerification = (dispatch: Dispatch<IAction>) => {
    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: ProductNames.CAR,
            dataKey: 'status',
            value: null
        }
    });
};

export const resetVehicle = ({ dispatch }: { dispatch: Dispatch<IAction> }) => {
    dispatch({
        type: RESET_VEHICLE,
        payload: {}
    });
};

export const setVehicle = ({
    dispatch,
    dmrData,
    selection
}: {
    dispatch: Dispatch<IAction>;
    dmrData: DMR;
    selection: Selection;
}) => {
    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: ProductNames.CAR,
            dataKey: 'dmr',
            value: dmrData
        }
    });
    setSelectionValue(dispatch, selection, {
        key: 'manufacturerName',
        value: dmrData.manufacturerName
    });
    setSelectionValue(dispatch, selection, {
        key: 'modelName',
        value: dmrData.modelName
    });
    setSelectionValue(dispatch, selection, {
        key: 'variantTypeName',
        value: dmrData.variantTypeName
    });
};

/**
 * @param registrationOrViNumber // Registration or Vehicle Indentify Number (VIN)
 * @param qualifier // Can be REG or VIN
 * @returns
 */
export const getVehicle = async (registrationOrViNumber: string, qualifier: VehicleQualifier) => {
    const url = `${apiGatewayBase}/v2/openapi/onlinesales-services/api/car/info?vehicleQualifier=${qualifier}&vehicleId=${registrationOrViNumber}`;
    return await genericRequest({
        method: 'GET',
        url: url,
        headers: wso2header
    });
};

export const updateMileage = async ({
    config,
    dispatch,
    selection,
    model,
    user
}: {
    config: IConfigProps;
    dispatch: Dispatch<IAction>;
    selection: Selection;
    model: Model;
    user: IUserContextProps;
}) => {
    const payload = {
        brand: 'TD',
        quoteIds: model[ProductNames.CAR].packages?.packages.map((p: Package) => p.quoteId),
        yearlyMillage: selection.mileage
    };
    await genericRequest(config.endpoint.car, payload);

    await updatePackages(
        {
            config,
            dispatch,
            selection,
            model,
            user
        },
        selection.packageInfo === PackageInfo.LIABILITY_ONLY ? { name: PackageInfo.LIABILITY_ONLY, value: true } : null
    );
};

export const setDmrTrackingUnitShown = ({
    shown,
    dispatch,
    dmrData
}: {
    shown: boolean;
    dispatch: Dispatch<IAction>;
    dmrData: DMR | null;
}) => {
    const payload = {
        ...dmrData,
        trackingUnitShown: shown
    };

    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: ProductNames.CAR,
            dataKey: 'dmr',
            value: payload
        }
    });
};

export const isEligibleForLeasing = (dateStr: string) => {
    const getYearDifference = (startDate: Date, endDate: Date) => {
        const millisecondsPerYear = 1000 * 60 * 60 * 24 * 365.25;
        const timeDifference = endDate.getTime() - startDate.getTime();
        const yearDifference = timeDifference / millisecondsPerYear;
        return Math.floor(yearDifference);
    };

    const maxYear = 4;
    const today = new Date();
    const otherDate = new Date(dateStr);
    return getYearDifference(otherDate, today) < maxYear;
};
